import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  FormControlLabel,
  Switch,
  Divider
} from '@mui/material';
import api from '../../../utils/api';
import useThemeStyles from '../../../theme/useThemeStyles';
import AddressMapSelector from '../AddressMapSelector';
import { supplierCategories, isInCategoryGroup } from '../../../config/supplierTaxonomy';

function Step3BusinessBasics({ formData, businessId, onComplete, onError }) {
  const styles = useThemeStyles();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [phase, setPhase] = useState('website'); // 'website' or 'review'
  const [scraping, setScraping] = useState(false);
  const [website, setWebsite] = useState(formData.website || '');
  const [localFormData, setLocalFormData] = useState(formData);
  const [addressType, setAddressType] = useState(formData.addressType || 'business');
  const [businessData, setBusinessData] = useState({
    name: formData.businessName || '',
    category: formData.category || '',
    primaryPhone: formData.primaryPhone || '',
    address: formData.address || '',
    description: formData.description || ''
  });
  const [venueDetails, setVenueDetails] = useState(formData.venueDetails || {});

  const isVenueCategory = isInCategoryGroup(businessData.category, 'venue');

  const handleWebsiteSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!website.trim()) {
      setError('Please enter your website URL');
      return;
    }

    setScraping(true);
    try {
      // Scrape website (no businessId required)
      const response = await api.post('/api/supplier/scrape-website', {
        website: website.trim()
      });

      if (response.data.status === 'success' && response.data.suggestions) {
        const suggestions = response.data.suggestions;
        
        // Populate form with scraped data
        setBusinessData({
          name: suggestions.businessName || '',
          category: suggestions.category || '',
          primaryPhone: suggestions.primaryPhone || '',
          address: suggestions.address || '',
          description: suggestions.description || ''
        });
        
        // Store scraped suggestions for use in other steps
        setLocalFormData(prev => ({
          ...prev,
          scrapedSuggestions: suggestions
        }));
        
        setPhase('review');
      } else {
        throw new Error('Failed to scrape website');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to scrape website. Please enter your details manually.');
      // Allow them to proceed manually
      setPhase('review');
    } finally {
      setScraping(false);
    }
  };

  const handleSkipWebsite = () => {
    setPhase('review');
  };

  const handleChange = (field) => (event) => {
    setBusinessData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    if (error) setError('');
  };

  const handleAddressChange = (address) => {
    setBusinessData(prev => ({
      ...prev,
      address: address
    }));
    if (error) setError('');
  };

  const handleAddressTypeChange = (type) => {
    setAddressType(type);
  };

  const handleCapacityToggle = (field) => (event) => {
    const checked = event.target.checked;
    setVenueDetails(prev => ({
      ...prev,
      [field]: checked
    }));
  };

  const handleCapacityChange = (field) => (event) => {
    const value = event.target.value;
    setVenueDetails(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate required fields
    if (!businessData.name || !businessData.category || !businessData.primaryPhone || !businessData.address) {
      setError('Please fill in all required fields: name, category, phone, and address');
      return;
    }

    try {
      setLoading(true);
      
      // Get scraped suggestions if available
      const scrapedSuggestions = localFormData.scrapedSuggestions || {};
      
      // If businessId exists, we're updating (user went back), otherwise creating new
      const endpoint = businessId 
        ? `/api/supplier/business/${businessId}` 
        : '/api/supplier/setup-business';
      const method = businessId ? 'put' : 'post';
      
      const payload = {
        name: businessData.name,
        category: businessData.category,
        description: businessData.description || '',
        contactInfo: {
          primaryPhone: businessData.primaryPhone,
          website: website || '',
          address: businessData.address,
          addressType: addressType
        },
        // Include business details if scraped
        businessDetails: (scrapedSuggestions.businessNumber || scrapedSuggestions.vatNumber) ? {
          businessNumber: scrapedSuggestions.businessNumber || '',
          vatNumber: scrapedSuggestions.vatNumber || ''
        } : undefined,
        // Store all scraped suggestions for future reference
        importSuggestions: Object.keys(scrapedSuggestions).length > 0 ? scrapedSuggestions : undefined
      };

      if (isVenueCategory) {
        payload.venueDetails = {
          ...(scrapedSuggestions.venueDetails || {}),
          ...venueDetails
        };
      }

      const response = await api[method](endpoint, payload);
      
      // Handle both create and update responses (structure is the same)
      const finalBusinessId = businessId || response.data.business.id;
      
      onComplete({
        ...formData,
        ...localFormData,
        businessName: businessData.name,
        category: businessData.category,
        primaryPhone: businessData.primaryPhone,
        address: businessData.address,
        addressType: addressType,
        website: website,
        description: businessData.description,
        businessId: finalBusinessId,
        venueDetails: isVenueCategory ? venueDetails : formData.venueDetails,
        // Pass along scraped data for other steps
        logoUrl: scrapedSuggestions.logoUrl || null,
        styleVibe: scrapedSuggestions.styleVibe || [],
        businessNumber: scrapedSuggestions.businessNumber || null,
        vatNumber: scrapedSuggestions.vatNumber || null
      }, finalBusinessId);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Business setup failed';
      setError(errorMessage);
      if (onError) onError(err);
    } finally {
      setLoading(false);
    }
  };

  // Phase 1: Website input
  if (phase === 'website') {
    return (
      <Box>
        <Typography variant="h5" sx={{ mb: 1, ...styles.text.primary }}>
          Tell us who you are
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, ...styles.text.secondary }}>
          Start by entering your website URL and we'll learn about your business automatically.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {scraping ? (
          <Paper sx={{ p: 6, textAlign: 'center', bgcolor: 'grey.50' }}>
            <CircularProgress size={48} sx={{ mb: 3 }} />
            <Typography variant="h6" sx={{ mb: 1, ...styles.text.primary }}>
              We're just learning about you...
            </Typography>
            <Typography variant="body2" sx={{ ...styles.text.secondary }}>
              Scanning your website and extracting your business details. This will only take a moment.
            </Typography>
          </Paper>
        ) : (
          <form onSubmit={handleWebsiteSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Your Website URL"
                  value={website}
                  onChange={(e) => {
                    setWebsite(e.target.value);
                    if (error) setError('');
                  }}
                  required
                  variant="outlined"
                  placeholder="https://yourwebsite.com"
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={!website.trim()}
                  sx={{ mb: 2 }}
                >
                  Continue
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleSkipWebsite}
                >
                  I don't have a website
                </Button>
              </Grid>
            </Grid>
          </form>
        )}
      </Box>
    );
  }

  // Phase 2: Review and edit scraped data
  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 1, ...styles.text.primary }}>
        Review Your Business Details
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, ...styles.text.secondary }}>
        We've filled in your details from your website. Please review and make any changes needed.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Business Name"
              value={businessData.name}
              onChange={handleChange('name')}
              required
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth required>
              <InputLabel>Category</InputLabel>
              <Select
                value={businessData.category}
                onChange={handleChange('category')}
                label="Category"
              >
                {supplierCategories.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Primary Phone"
              value={businessData.primaryPhone}
              onChange={handleChange('primaryPhone')}
              required
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12}>
            <AddressMapSelector
              label="Business Address"
              placeholder="Enter your business address..."
              value={businessData.address}
              onChange={handleAddressChange}
              addressType={addressType}
              onAddressTypeChange={handleAddressTypeChange}
              required
              helperText="Select business or private address. Private addresses are hidden on the map for privacy."
            />
          </Grid>
          {website && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Website"
                value={website}
                disabled
                variant="outlined"
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description (Optional)"
              value={businessData.description}
              onChange={handleChange('description')}
              variant="outlined"
              multiline
              rows={3}
              placeholder="Brief description of your business"
            />
          </Grid>
          {isVenueCategory && (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Capacities
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                  sx={{
                    pl: { xs: 1, sm: 0 },
                    '& .MuiSwitch-root': { mr: 1 }
                  }}
                  control={
                    <Switch
                      checked={venueDetails?.canHostCeremony || false}
                      onChange={handleCapacityToggle('canHostCeremony')}
                      color="primary"
                    />
                  }
                  label="Can host ceremonies?"
                />
                {venueDetails?.canHostCeremony && (
                  <Box sx={{ mt: 1, ml: { xs: 1, sm: 4 }, maxWidth: { xs: '100%', sm: 280 } }}>
                    <TextField
                      fullWidth
                      sx={{ mt: 1 }}
                      label="Ceremony capacity"
                      type="number"
                      value={venueDetails?.ceremonyCapacity ?? ''}
                      onChange={handleCapacityChange('ceremonyCapacity')}
                      helperText="Maximum number of guests for ceremony"
                      inputProps={{ min: 0 }}
                    />
                  </Box>
                )}
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                  sx={{
                    pl: { xs: 1, sm: 0 },
                    '& .MuiSwitch-root': { mr: 1 }
                  }}
                  control={
                    <Switch
                      checked={venueDetails?.canHostReception || false}
                      onChange={handleCapacityToggle('canHostReception')}
                      color="primary"
                    />
                  }
                  label="Can host receptions?"
                />
                {venueDetails?.canHostReception && (
                  <Box sx={{ mt: 1, ml: { xs: 1, sm: 4 }, maxWidth: { xs: '100%', sm: 280 } }}>
                    <TextField
                      fullWidth
                      sx={{ mt: 1 }}
                      label="Reception capacity"
                      type="number"
                      value={venueDetails?.receptionCapacity ?? ''}
                      onChange={handleCapacityChange('receptionCapacity')}
                      helperText="Maximum number of guests for reception"
                      inputProps={{ min: 0 }}
                    />
                  </Box>
                )}
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                  sx={{
                    pl: { xs: 1, sm: 0 },
                    '& .MuiSwitch-root': { mr: 1 }
                  }}
                  control={
                    <Switch
                      checked={venueDetails?.canHostEvening || false}
                      onChange={handleCapacityToggle('canHostEvening')}
                      color="primary"
                    />
                  }
                  label="Can host evening celebrations?"
                />
                {venueDetails?.canHostEvening && (
                  <Box sx={{ mt: 1, ml: 4, maxWidth: 280 }}>
                    <TextField
                      fullWidth
                      sx={{ mt: 1 }}
                      label="Evening capacity"
                      type="number"
                      value={venueDetails?.eveningCapacity ?? ''}
                      onChange={handleCapacityChange('eveningCapacity')}
                      helperText="Maximum number of guests for evening"
                      inputProps={{ min: 0 }}
                    />
                  </Box>
                )}
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{ mt: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Continue'}
            </Button>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
}

export default Step3BusinessBasics;
